/**
 * P0 TENANT ISOLATION: Canonical tenant-aware path builder
 * 
 * This is the ONLY place tenant_id should be added to navigation paths.
 * All navigation helpers MUST use this utility.
 * 
 * Guarantees:
 * - Never generates duplicate tenant_id params
 * - Self-heals malformed URLs with multiple tenant_id values
 * - Preserves all other query params
 * - Deterministic output
 */

/**
 * Build a tenant-aware navigation path.
 * 
 * @param {string} path - Base path (may include existing query params)
 * @param {string|null} tenantId - Tenant ID to set (if null, returns path unchanged)
 * @returns {string} Path with tenant_id properly set
 * 
 * @example
 * // Replace existing tenant_id
 * buildTenantPath("/app/leads?tenant_id=AAA", "BBB")
 * // Returns: "/app/leads?tenant_id=BBB"
 * 
 * @example
 * // Preserve other query params
 * buildTenantPath("/app/leads?status=open&q=smith&tenant_id=AAA", "BBB")
 * // Returns: "/app/leads?status=open&q=smith&tenant_id=BBB"
 * 
 * @example
 * // Add tenant_id if missing
 * buildTenantPath("/app/leads?status=open", "BBB")
 * // Returns: "/app/leads?status=open&tenant_id=BBB"
 */
export function buildTenantPath(path, tenantId) {
  if (!tenantId) {
    return path;
  }

  try {
    const url = new URL(path, window.location.origin);
    
    // P0 DEFENSIVE GUARD: Self-heal malformed URLs with duplicate tenant_id
    const existingValues = url.searchParams.getAll("tenant_id");
    if (existingValues.length > 1) {
      // Remove all existing tenant_id params
      url.searchParams.delete("tenant_id");
    }
    
    // Always use set() - never append()
    // This replaces any existing tenant_id with the new value
    url.searchParams.set("tenant_id", tenantId);
    
    // Return normalized path
    const search = url.searchParams.toString();
    return search ? `${url.pathname}?${search}` : url.pathname;
  } catch {
    // Fallback for edge cases - should never happen in practice
    console.warn("[buildTenantPath] Failed to parse path:", path);
    return path;
  }
}

/**
 * Get tenant_id from current browser URL.
 * 
 * @returns {string|null} Current tenant_id or null
 */
export function getCurrentTenantId() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("tenant_id") || null;
}

/**
 * Build path preserving current tenant context.
 * Convenience wrapper that uses current URL's tenant_id.
 * 
 * @param {string} path - Base path
 * @returns {string} Path with current tenant_id
 */
export function buildPathWithCurrentTenant(path) {
  return buildTenantPath(path, getCurrentTenantId());
}
